// =============================================================================
//  desfaseDeSegmentos.js
//  MOVER EL VÍDEO ENTERO DE UNA VEZ
//
//  Rob, 14-10-2026: «sería de gran ayuda poner lo mismo −1s −.5 −.1 │ +.1 +.5
//  +1s pero aquí la magia: si activo uno de esos botones se aplica a TODOS en un
//  solo clic. Me he topado que a veces todos tienen un desfase pequeño de .5 s y
//  tengo que abrir todos para ajustar esos segundos y me quita mucho tiempo...
//  siempre me pasa al terminar las oraciones, digamos al final. Porque empieza
//  bien pero al final de la oración se come tiempo».
//
//  ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
//  El transcript de YouTube no viene desfasado al azar: viene desfasado IGUAL en
//  todo el vídeo, porque el desfase lo pone el sistema que transcribió, no cada
//  frase. Arreglarlo fragmento a fragmento es hacer 57 veces la misma cuenta a
//  mano. Esto la hace una vez.
//
//  ── LO QUE UNA CANCIÓN NO TIENE Y UN VÍDEO SÍ ───────────────────────────────
//  En una canción mover la línea es mover UN número: su `time`. En un vídeo cada
//  fragmento tiene principio y final, y además cada pregunta lleva DENTRO el
//  segundo exacto en el que salta (`data.trigger_seconds`).
//
//  Si se movieran los fragmentos y no los disparos, el ajuste saldría peor que
//  antes: el texto correría medio segundo y las preguntas se quedarían en el
//  sitio viejo, así que el desfase que Rob vino a quitar reaparecería justo donde
//  más se nota —cuando el vídeo se para a preguntar—. Por eso aquí se mueve TODO
//  lo que es un segundo del vídeo, de una pieza.
//
//  A un ejercicio que todavía no tiene disparo propio se le ESCRIBE el que le
//  pondría el servidor (`disparoPorDefecto`), ya movido. Queda en el mismo
//  segundo en el que estaba antes del desfase: no le cambia el comportamiento a
//  nadie, sólo deja de depender de una cuenta que se haría más tarde con el
//  principio ya movido —y entonces saldría otro número.
//
//  ── POR QUÉ ESTÁ SUELTO DEL DIBUJO ──────────────────────────────────────────
//  Porque un botón que mueve 57 fragmentos y sus preguntas a la vez es el que más
//  trabajo puede estropear de un clic, y suelto se mide sin navegador.
// =============================================================================

import { disparoPorDefecto } from './tiemposDeDisparo.js';

/** Décimas: lo más fino que el reloj de YouTube cumple de verdad. */
export const r1 = (s) => Math.round((Number(s) || 0) * 10) / 10;

const aNumero = (v) => {
    //  `Number(null)` es 0 y `Number('')` también. Un fragmento a medio montar
    //  contaría como el segundo cero y haría de suelo de todo el vídeo: el
    //  desfase hacia atrás saldría recortado a cero y el botón parecería roto.
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

/** El disparo que tiene un ejercicio AHORA: el suyo, o el que le toca. */
const disparoVigente = (ex, inicio) => {
    if (!ex || !ex.data || typeof ex.data !== 'object') return null;
    const propio = ex.data.trigger_seconds;
    if (typeof propio === 'number' && Number.isFinite(propio) && propio >= 0) {
        return r1(propio);
    }
    return disparoPorDefecto(inicio);
};

/**
 * Todos los segundos del vídeo que este botón movería.
 *
 * Hace falta la lista completa —principios, finales y disparos— porque el
 * recorte y la aplicación tienen que mirar lo MISMO. Si el recorte sólo mirase
 * los fragmentos, un disparo podría salirse del final del vídeo sin que nada lo
 * frenara: y un ejercicio que salta después del final no salta nunca.
 */
export function segundosQueSeMueven(segments) {
    const lista = Array.isArray(segments) ? segments : [];
    const fuera = [];
    for (const seg of lista) {
        const a = aNumero(seg?.startTime);
        const b = aNumero(seg?.endTime);
        if (a !== null) fuera.push(a);
        if (b !== null) fuera.push(b);
        for (const ex of (seg?.exercises || [])) {
            const d = disparoVigente(ex, seg?.startTime);
            if (d !== null) fuera.push(d);
        }
    }
    return fuera;
}

/**
 * Los segundos que tienen que caber DENTRO del vídeo: los principios y los
 * disparos.
 *
 * El final del último fragmento no entra, y es a propósito. Un fragmento que
 * acaba medio segundo después de que el vídeo termine no rompe nada: el vídeo se
 * acaba y ya está. Lo que no puede pasar es que un fragmento EMPIECE después del
 * final —no suena nunca— ni que una pregunta salte ahí —no salta nunca—.
 *
 * Contar los finales era lo que dejaba el botón muerto en cuanto el último
 * fragmento llegaba al final del vídeo, que es lo normal: el troceo le pone de
 * final el final del vídeo. Con eso, el tope saltaba siempre y por nada.
 */
export function segundosQueDebenCaberDentro(segments) {
    const lista = Array.isArray(segments) ? segments : [];
    const fuera = [];
    for (const seg of lista) {
        const a = aNumero(seg?.startTime);
        if (a !== null) fuera.push(a);
        for (const ex of (seg?.exercises || [])) {
            const d = disparoVigente(ex, seg?.startTime);
            if (d !== null) fuera.push(d);
        }
    }
    return fuera;
}

/**
 * Cuánto desfase CABE de verdad.
 *
 * Esta es la lección que costó una tarde en el editor de canciones: con un
 * negativo grande, `Math.max(0, …)` aplasta las primeras marcas contra el cero
 * —tres fragmentos distintos acabando en 0:00.0— y eso ya no lo arregla el botón
 * de quitar el desfase, porque al aplastarlas se perdió dónde estaba cada una.
 *
 * Así que el desfase se recorta ANTES de aplicarlo: se mueve lo que se pueda
 * mover de una pieza, sin que nada se salga por abajo ni por el final del vídeo.
 * Moverlo todo junto es lo que lo hace reversible al décimo.
 */
export function desfaseQueCabe(segments, delta, duracion = 0) {
    const d = Number(delta);
    if (!Number.isFinite(d) || d === 0) return 0;
    const tiempos = segundosQueSeMueven(segments);
    if (!tiempos.length) return 0;

    if (d < 0) return r1(Math.max(d, -Math.min(...tiempos)));

    const total = aNumero(duracion);
    //  Sin duración —el reproductor de YouTube todavía no ha contestado— no hay
    //  techo que comprobar. Frenar por no saber sería frenar sin motivo.
    if (total === null || total <= 0) return r1(d);
    const dentro = segundosQueDebenCaberDentro(segments);
    if (!dentro.length) return r1(d);
    return r1(Math.max(0, Math.min(d, total - Math.max(...dentro))));
}

/**
 * Por qué no cupo todo lo que se pidió, para poder DECIRLO.
 *
 * Un botón que mueve menos de lo que anuncia y se calla es, a ojos de quien lo
 * pulsa, un botón roto. Devuelve `null` cuando cupo entero.
 */
export function motivoDelRecorte(segments, delta, duracion = 0) {
    const pedido = r1(delta);
    const cabe = desfaseQueCabe(segments, delta, duracion);
    if (cabe === pedido) return null;
    const tiempos = segundosQueSeMueven(segments);
    if (!tiempos.length) return 'no hay ningún fragmento con tiempo que mover.';
    if (pedido < 0) {
        return `la marca más temprana está en ${r1(Math.min(...tiempos))} s: hacia `
            + `atrás sólo caben ${Math.abs(cabe)} s sin que nada quede antes de que `
            + `empiece el vídeo.`;
    }
    const dentro = segundosQueDebenCaberDentro(segments);
    const ultima = dentro.length ? r1(Math.max(...dentro)) : r1(Math.max(...tiempos));
    return `el último fragmento empieza en ${ultima} s y el vídeo dura `
        + `${r1(duracion)} s: hacia adelante sólo caben ${cabe} s sin que nada `
        + `empiece después del final.`;
}

/**
 * Cuántas marcas quedarían FUERA del vídeo moviéndolo `delta`.
 *
 * Con `delta` a cero dice cuántas estaban fuera YA, que es la pregunta que de
 * verdad importa: un transcript más largo que el vídeo llega roto de casa, y
 * entonces el tope no está defendiendo nada — el daño ya existía y no lo hizo
 * este botón.
 */
export function marcasFueraDelVideo(segments, duracion, delta = 0) {
    const total = aNumero(duracion);
    const d = r1(delta);
    const vacio = { fragmentos: 0, disparos: 0, total: 0 };
    if (total === null || total <= 0) return vacio;

    let fragmentos = 0;
    let disparos = 0;
    for (const seg of (Array.isArray(segments) ? segments : [])) {
        const a = aNumero(seg?.startTime);
        const b = aNumero(seg?.endTime);
        //  El principio es el que manda: un fragmento que EMPIEZA después del
        //  final no suena nunca. Que acabe un poco más allá es sólo el último,
        //  y eso pasa en vídeos perfectamente normales.
        if (a !== null && r1(a + d) > total) fragmentos += 1;
        else if (a === null && b !== null && r1(b + d) > total) fragmentos += 1;
        for (const ex of (seg?.exercises || [])) {
            const t = disparoVigente(ex, seg?.startTime);
            if (t !== null && r1(t + d) > total) disparos += 1;
        }
    }
    return { fragmentos, disparos, total: fragmentos + disparos };
}

/**
 * Todo lo que hay que saber ANTES de mover: qué cabe, qué no, y por qué.
 *
 * Rob, 14-10-2026: «le di al +.5 y me marcó errores, lo cual es correcto... pero
 * por eso ese botón increíble dejó de funcionar. Lo ideal es que el sistema sea
 * más inteligente y me avise, yo confirmo si hacerlo de todos modos».
 *
 * Tenía razón, y el error de diseño era mío otra vez. Los dos topes NO son
 * iguales, y tratarlos igual fue el fallo:
 *
 *  · **Por abajo** no se puede pasar: un segundo negativo no existe. Ahí se
 *    mueve lo máximo que cabe, de una pieza, y ya está.
 *  · **Por el final** sí se puede: una marca pasada del final es una marca
 *    pasada del final, nada más. Se deshace moviendo para atrás. Y en el caso
 *    de Rob —99 fragmentos que acaban en el 5:21 de un vídeo de 2:59— ya
 *    estaban fuera ANTES de tocar nada: el tope no defendía nada y lo único
 *    que hacía era dejar el botón muerto.
 *
 * Así que por el final se avisa y decide él.
 */
export function diagnosticoDelDesfase(segments, delta, duracion = 0) {
    const pedido = r1(delta);
    const cabe = desfaseQueCabe(segments, delta, duracion);
    return {
        pedido,
        cabe,
        entero: cabe === pedido,
        //  Hacia adelante se puede insistir; hacia atrás no hay nada al otro
        //  lado del cero a lo que insistir.
        sePuedeInsistir: pedido > 0 && cabe !== pedido,
        motivo: motivoDelRecorte(segments, delta, duracion),
        yaFuera: marcasFueraDelVideo(segments, duracion, 0),
        quedarianFuera: marcasFueraDelVideo(segments, duracion, pedido),
        fragmentos: (Array.isArray(segments) ? segments : []).length,
    };
}

/**
 * Mover el vídeo entero: principios, finales y disparos, de una pieza.
 *
 * Devuelve unos segmentos NUEVOS y la cuenta de lo que se tocó, porque hay que
 * poder decir qué acaba de pasar: 57 fragmentos moviéndose a la vez sin un aviso
 * que lo nombre no se distingue de un accidente.
 */
export function aplicaDesfase(segments, delta) {
    const d = r1(delta);
    const origen = Array.isArray(segments) ? segments : [];
    if (!d || !origen.length) {
        return { segments: origen, fragmentos: 0, disparos: 0, escritos: 0 };
    }

    let fragmentos = 0;
    let disparos = 0;
    let escritos = 0;

    const nuevos = origen.map((seg) => {
        const a = aNumero(seg?.startTime);
        const b = aNumero(seg?.endTime);
        const fuera = { ...seg };
        if (a !== null || b !== null) fragmentos += 1;
        if (a !== null) fuera.startTime = r1(a + d);
        if (b !== null) fuera.endTime = r1(b + d);

        const ejercicios = seg?.exercises;
        if (Array.isArray(ejercicios) && ejercicios.length) {
            fuera.exercises = ejercicios.map((ex) => {
                //  El disparo se calcula con el principio VIEJO: es el segundo en
                //  el que el ejercicio salta ahora mismo, y lo que se mueve es ése.
                const ahora = disparoVigente(ex, seg?.startTime);
                if (ahora === null) return ex;
                if (typeof ex.data.trigger_seconds !== 'number') escritos += 1;
                disparos += 1;
                return {
                    ...ex,
                    data: { ...ex.data, trigger_seconds: r1(Math.max(0, ahora + d)) },
                };
            });
        }
        return fuera;
    });

    return { segments: nuevos, fragmentos, disparos, escritos };
}
